import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  UniqueIdentifier,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  MutableRefObject,
} from 'react';
import { Button, Dropdown, Form, Modal } from 'react-bootstrap';
import SoundCategoryList from '../SoundCategoryList';
import SoundLibrary from '../../services/SoundLibrary';
import { SupportedStatus } from '../../services/SoundLibrary/supportedStatus';
import SoundManager from '../../services/SoundManager';
import { SoundInstruction } from '../../services/Sequencer/sequence';
import { buildSequenceTimeline } from '../../services/Sequencer/scheduler';
import {
  DEFAULT_DELAY_TICKS,
  MAX_DELAY_TICKS,
  MAX_SOUND_PITCH,
  MAX_SOUND_VOLUME,
  MIN_DELAY_TICKS,
  MIN_SOUND_PITCH,
  MIN_SOUND_VOLUME,
  SequencerInstruction,
  createInitialSequencerState,
  exportSequenceJson,
  parseSequenceJson,
  sequencerReducer,
} from '../../services/Sequencer/sequence';
import './SoundSequencer.scss';

interface SequencerWorkspaceProps {
  search: string;
}

interface DragData {
  kind: 'sound-source' | 'utility-delay' | 'instruction';
  soundEvent?: string;
  displayName?: string;
  instructionId?: string;
}

interface PlaybackState {
  running: boolean;
  activeIds: Set<string>;
  failedIds: Set<string>;
}

export default function SequencerWorkspace({ search }: SequencerWorkspaceProps) {
  const [state, dispatch] = useReducer(sequencerReducer, undefined, createInitialSequencerState);
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [toastMessage, setToastMessage] = useState<string>();
  const [sequencerHeight, setSequencerHeight] = useState<number>();
  const [playback, setPlayback] = useState<PlaybackState>({
    running: false,
    activeIds: new Set(),
    failedIds: new Set(),
  });
  const sequencerRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const instructionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const timersRef = useRef<number[]>([]);
  const stopsRef = useRef<(() => void)[]>([]);
  const idCounter = useRef(0);

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 6 },
  }));

  const playableSoundEvents = useMemo(
    () => new Set(SoundLibrary.categories
      .filter(category => category.supported !== SupportedStatus.NONE)
      .map(category => category.name)),
    []
  );

  const nextId = useCallback((prefix = 'instruction') => {
    idCounter.current += 1;
    return `${prefix}-${Date.now()}-${idCounter.current}`;
  }, []);

  const setInstructionRef = useCallback((id: string) => (node: HTMLDivElement | null) => {
    instructionRefs.current[id] = node;
  }, []);

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
    window.setTimeout(() => {
      setToastMessage(current => current === message ? undefined : current);
    }, 4200);
  }, []);

  const stopPlaybackRuntime = useCallback((stopStartedSounds: boolean) => {
    timersRef.current.forEach(timer => window.clearTimeout(timer));
    timersRef.current = [];

    if (stopStartedSounds) {
      stopsRef.current.forEach(stop => stop());
    }
    stopsRef.current = [];

    dispatch({ type: 'SET_LOCKED', locked: false });
    setPlayback(current => ({
      running: false,
      activeIds: new Set(),
      failedIds: current.failedIds,
    }));
  }, []);

  const finishPlayback = useCallback(() => {
    stopPlaybackRuntime(false);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stopPlaybackRuntime]);

  useEffect(() => () => stopPlaybackRuntime(true), [stopPlaybackRuntime]);

  useEffect(() => {
    let animationFrame = 0;

    const updateHeight = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        if (!sequencerRef.current) return;

        const navbar = document.querySelector('.navbar.fixed-bottom');
        const navbarTop = navbar?.getBoundingClientRect().top ?? window.innerHeight;
        const sequencerTop = sequencerRef.current.getBoundingClientRect().top;
        setSequencerHeight(Math.max(360, Math.floor(navbarTop - sequencerTop - 8)));
      });
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', updateHeight, true);
    };
  }, []);

  useEffect(() => {
    if (!state.lastUndo) return;

    const timer = window.setTimeout(() => {
      dispatch({ type: 'DISMISS_UNDO' });
    }, 4200);

    return () => window.clearTimeout(timer);
  }, [state.lastUndo]);

  useEffect(() => {
    const firstActiveId = Array.from(playback.activeIds)[0];
    if (!firstActiveId) return;
    instructionRefs.current[firstActiveId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [playback.activeIds]);

  const scheduleTimer = useCallback((fn: () => void, delayMs: number) => {
    const timer = window.setTimeout(fn, delayMs);
    timersRef.current.push(timer);
    return timer;
  }, []);

  const playSoundInstruction = useCallback((instruction: SoundInstruction) => {
    const category = SoundLibrary.getCategory(instruction.soundEvent);
    const randomSound = category?.randomSound();

    if (!randomSound) {
      setPlayback(current => ({
        ...current,
        failedIds: new Set([...Array.from(current.failedIds), instruction.id]),
      }));
      showToast(`Could not play ${instruction.soundEvent}.`);
      return;
    }

    const stop = SoundManager.playFromUrlNew({
      url: randomSound.soundLocation,
      volume: instruction.volume * randomSound.volume,
      pitch: instruction.pitch * randomSound.pitch,
      remember: false,
      onStart(startedStop) {
        stopsRef.current.push(startedStop);
      },
      onError(error) {
        setPlayback(current => ({
          ...current,
          failedIds: new Set([...Array.from(current.failedIds), instruction.id]),
        }));
        showToast(`Audio failed for ${instruction.soundEvent}.`);
        // eslint-disable-next-line no-console
        console.error('Sequencer audio failure', error);
      },
    });
    stopsRef.current.push(stop);
  }, [showToast]);

  const playSequence = useCallback(() => {
    if (state.locked || !state.instructions.length) return;

    const timeline = buildSequenceTimeline(state.instructions);
    const instructionById = new Map(state.instructions.map(instruction => [instruction.id, instruction]));
    let endAtMs = 0;

    dispatch({ type: 'SET_LOCKED', locked: true });
    setPlayback({
      running: true,
      activeIds: new Set(),
      failedIds: new Set(),
    });

    for (const entry of timeline) {
      if (entry.type === 'sound-group') {
        endAtMs = Math.max(endAtMs, entry.atMs);
        scheduleTimer(() => {
          setPlayback(current => ({ ...current, activeIds: new Set(entry.instructionIds) }));

          for (const instructionId of entry.instructionIds) {
            const instruction = instructionById.get(instructionId);
            if (instruction?.type === 'sound') playSoundInstruction(instruction);
          }

          scheduleTimer(() => {
            setPlayback(current => ({ ...current, activeIds: new Set() }));
          }, 260);
        }, entry.atMs);
      } else {
        endAtMs = Math.max(endAtMs, entry.atMs + entry.durationMs);
        scheduleTimer(() => {
          setPlayback(current => ({ ...current, activeIds: new Set([entry.instructionId]) }));
        }, entry.atMs);
        scheduleTimer(() => {
          setPlayback(current => ({ ...current, activeIds: new Set() }));
        }, entry.atMs + entry.durationMs);
      }
    }

    scheduleTimer(finishPlayback, endAtMs + 320);
  }, [
    finishPlayback,
    playSoundInstruction,
    scheduleTimer,
    state.instructions,
    state.locked,
  ]);

  const onDragStart = useCallback(({ active }: DragStartEvent) => {
    const dragData = active.data.current as DragData | undefined;
    if (!dragData) return;

    if (dragData.kind === 'instruction' && dragData.instructionId) {
      const instruction = state.instructions.find(it => it.id === dragData.instructionId);
      setActiveDrag({
        ...dragData,
        displayName: instruction ? getInstructionTitle(instruction) : dragData.displayName,
      });
      return;
    }

    setActiveDrag(dragData);
  }, [state.instructions]);

  const onDragEnd = useCallback(({ active, over }: DragEndEvent) => {
    const dragData = active.data.current as DragData | undefined;
    const dropIndex = getDropIndex(over?.id, state.instructions);
    setActiveDrag(null);

    if (state.locked || !dragData) return;

    if (dragData.kind === 'sound-source' && dragData.soundEvent && typeof dropIndex === 'number') {
      dispatch({
        type: 'ADD_SOUND',
        id: nextId('sound'),
        soundEvent: dragData.soundEvent,
        index: dropIndex,
      });
      return;
    }

    if (dragData.kind === 'utility-delay' && typeof dropIndex === 'number') {
      dispatch({
        type: 'ADD_DELAY',
        id: nextId('delay'),
        ticks: DEFAULT_DELAY_TICKS,
        index: dropIndex,
      });
      return;
    }

    if (dragData.kind === 'instruction' && dragData.instructionId) {
      if (typeof dropIndex === 'number') {
        dispatch({
          type: 'MOVE_INSTRUCTION',
          id: dragData.instructionId,
          index: dropIndex,
        });
      } else {
        dispatch({
          type: 'DELETE_INSTRUCTION',
          id: dragData.instructionId,
          undoable: true,
        });
      }
    }
  }, [nextId, state.instructions, state.locked]);

  const copyJson = useCallback(async () => {
    const json = exportSequenceJson(state.instructions);

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(json);
        showToast('Sequence JSON copied.');
        return;
      } catch {
        // Fall through to the manual prompt.
      }
    }

    window.prompt('Copy sequence JSON', json);
  }, [showToast, state.instructions]);

  const importJson = useCallback(() => {
    const result = parseSequenceJson(importText, playableSoundEvents, () => nextId('import'));

    if (!result.ok) {
      showToast(result.error);
      return;
    }

    dispatch({ type: 'IMPORT_SEQUENCE', instructions: result.instructions });
    setImportOpen(false);
    setImportText('');
    showToast('Sequence imported.');
  }, [importText, nextId, playableSoundEvents, showToast]);

  const onClickWait = useCallback(() => {
    if (state.locked) return;
    dispatch({
      type: 'ADD_DELAY',
      id: nextId('delay'),
      ticks: DEFAULT_DELAY_TICKS,
      index: state.instructions.length,
    });
  }, [nextId, state.instructions.length, state.locked]);

  return <DndContext
    sensors={sensors}
    collisionDetection={sequencerCollisionDetection}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onDragCancel={() => setActiveDrag(null)}
  >
    <div className="modulator-workspace">
      <div className="sound-list-pane">
        <SoundCategoryList search={search} draggingDisabled={state.locked}/>
      </div>
      <aside
        className="sound-sequencer"
        aria-label="Sound sequencer"
        ref={sequencerRef}
        style={sequencerHeight
          ? ({ '--sequencer-height': `${sequencerHeight}px` } as CSSProperties)
          : undefined}
      >
        <div className="sound-sequencer__header">
          <div>
            <h3>Sequencer</h3>
            <span>{state.instructions.length} instruction{state.instructions.length === 1 ? '' : 's'}</span>
          </div>
          <Dropdown align="end">
            <Dropdown.Toggle variant="outline-light" size="sm" disabled={state.locked}>
              <i className="bi-three-dots-vertical"/>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={copyJson} disabled={!state.instructions.length}>Copy JSON</Dropdown.Item>
              <Dropdown.Item onClick={() => setImportOpen(true)}>Import JSON</Dropdown.Item>
              <Dropdown.Divider/>
              <Dropdown.Item
                className="text-danger"
                disabled={!state.instructions.length}
                onClick={() => dispatch({ type: 'CLEAR_ALL', undoable: true })}
              >
                Clear all
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="sound-sequencer__utilities">
          <UtilityDelayCard disabled={state.locked} onClick={onClickWait}/>
        </div>

        <SequencerListDropArea disabled={state.locked} listRef={listRef}>
          <SortableContext
            items={state.instructions.map(instruction => instruction.id)}
            strategy={verticalListSortingStrategy}
          >
            <SequenceDropSlot index={0} disabled={state.locked}/>
            {state.instructions.map((instruction, index) => (
              <React.Fragment key={instruction.id}>
                <SequencerCard
                  instruction={instruction}
                  disabled={state.locked}
                  active={playback.activeIds.has(instruction.id)}
                  failed={playback.failedIds.has(instruction.id)}
                  setRef={setInstructionRef(instruction.id)}
                  onDelete={() => dispatch({
                    type: 'DELETE_INSTRUCTION',
                    id: instruction.id,
                    undoable: true,
                  })}
                  onPreview={() => {
                    if (instruction.type === 'sound') playSoundInstruction(instruction);
                  }}
                  onUpdateSound={(volume, pitch) => dispatch({
                    type: 'UPDATE_SOUND',
                    id: instruction.id,
                    volume,
                    pitch,
                  })}
                  onUpdateDelay={ticks => dispatch({
                    type: 'UPDATE_DELAY',
                    id: instruction.id,
                    ticks,
                  })}
                />
                <SequenceDropSlot index={index + 1} disabled={state.locked}/>
              </React.Fragment>
            ))}
          </SortableContext>
          {!state.instructions.length && <EmptyDropZone disabled={state.locked}/>}
        </SequencerListDropArea>

        <div className="sound-sequencer__footer">
          <Button
            variant={playback.running ? 'danger' : 'success'}
            disabled={!playback.running && !state.instructions.length}
            onClick={() => playback.running ? stopPlaybackRuntime(true) : playSequence()}
          >
            <i className={`bi-${playback.running ? 'stop-fill' : 'play-fill'}`}/>
            {playback.running ? 'Stop' : 'Play'}
          </Button>
        </div>
      </aside>
    </div>

    <DragOverlay>
      {activeDrag && <DragPreview dragData={activeDrag}/>}
    </DragOverlay>

    {state.lastUndo && !state.locked && <div className="sequencer-toast sequencer-toast--undo">
      <span>{state.lastUndo.message}</span>
      <Button variant="link" size="sm" onClick={() => dispatch({ type: 'UNDO_LAST_DELETE' })}>Undo</Button>
    </div>}

    {toastMessage && <div className="sequencer-toast">
      <span>{toastMessage}</span>
      <Button variant="link" size="sm" onClick={() => setToastMessage(undefined)}>Dismiss</Button>
    </div>}

    <Modal show={importOpen} onHide={() => setImportOpen(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Import Sequence JSON</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          as="textarea"
          rows={10}
          value={importText}
          disabled={state.locked}
          onChange={event => setImportText(event.target.value)}
          placeholder='{"version":1,"instructions":[]}'
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setImportOpen(false)}>Cancel</Button>
        <Button variant="primary" disabled={state.locked || !importText.trim()} onClick={importJson}>Import</Button>
      </Modal.Footer>
    </Modal>
  </DndContext>;
}

function SequencerListDropArea({
  children,
  disabled,
  listRef,
}: {
  children: React.ReactNode;
  disabled: boolean;
  listRef: MutableRefObject<HTMLDivElement | null>;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'sequence-list',
    disabled,
  });

  return <div
    className={classNames('sound-sequencer__list', isOver && 'is-over')}
    ref={node => {
      listRef.current = node;
      setNodeRef(node);
    }}
  >
    {children}
  </div>;
}

function UtilityDelayCard({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: 'utility-delay',
    data: { kind: 'utility-delay', displayName: 'Wait' },
    disabled,
  });

  return <button
    type="button"
    ref={setNodeRef}
    className={classNames('sequencer-utility-card', isDragging && 'is-dragging')}
    disabled={disabled}
    onClick={onClick}
    {...attributes}
    {...listeners}
  >
    <i className="bi-clock"/>
    <span>Wait</span>
  </button>;
}

function SequenceDropSlot({ index, disabled }: { index: number; disabled: boolean }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `slot:${index}`,
    disabled,
  });

  return <div
    ref={setNodeRef}
    className={classNames('sequencer-drop-slot', isOver && 'is-over')}
    aria-hidden="true"
  />;
}

function EmptyDropZone({ disabled }: { disabled: boolean }) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'sequence-empty',
    disabled,
  });

  return <div ref={setNodeRef} className={classNames('sequencer-empty-zone', isOver && 'is-over')}>
    <i className="bi-music-note-list"/>
    <span>Drop sounds or wait cards here</span>
  </div>;
}

interface SequencerCardProps {
  instruction: SequencerInstruction;
  disabled: boolean;
  active: boolean;
  failed: boolean;
  setRef: (node: HTMLDivElement | null) => void;
  onDelete: () => void;
  onPreview: () => void;
  onUpdateSound: (volume?: number, pitch?: number) => void;
  onUpdateDelay: (ticks: number) => void;
}

function SequencerCard({
  instruction,
  disabled,
  active,
  failed,
  setRef,
  onDelete,
  onPreview,
  onUpdateSound,
  onUpdateDelay,
}: SequencerCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: instruction.id,
    data: {
      kind: 'instruction',
      instructionId: instruction.id,
      displayName: getInstructionTitle(instruction),
    },
    disabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const ref = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setRef(node);
  };

  return <div
    ref={ref}
    style={style}
    className={classNames(
      'sequencer-card',
      `sequencer-card--${instruction.type}`,
      active && 'is-active',
      failed && 'has-failed',
      isDragging && 'is-dragging'
    )}
  >
    <div className="sequencer-card__top">
      <button
        type="button"
        className="sequencer-card__handle"
        disabled={disabled}
        title="Drag instruction"
        {...attributes}
        {...listeners}
      >
        <i className="bi-grip-vertical"/>
      </button>
      <div className="sequencer-card__title">
        <strong>{getInstructionTitle(instruction)}</strong>
        {instruction.type === 'sound' && <code>{instruction.soundEvent}</code>}
      </div>
      {instruction.type === 'sound' && <Button
        variant="outline-success"
        size="sm"
        disabled={disabled}
        title="Preview sound"
        onClick={onPreview}
      >
        <i className="bi-play-fill"/>
      </Button>}
      <Button
        variant="outline-danger"
        size="sm"
        disabled={disabled}
        title="Delete instruction"
        onClick={onDelete}
      >
        <i className="bi-x-lg"/>
      </Button>
    </div>

    {instruction.type === 'sound'
      ? <SoundCardControls instruction={instruction} disabled={disabled} onUpdate={onUpdateSound}/>
      : <DelayCardControls instruction={instruction} disabled={disabled} onUpdate={onUpdateDelay}/>}
  </div>;
}

function SoundCardControls({
  instruction,
  disabled,
  onUpdate,
}: {
  instruction: SoundInstruction;
  disabled: boolean;
  onUpdate: (volume?: number, pitch?: number) => void;
}) {
  return <div className="sequencer-card__controls">
    <label>
      <span>Vol</span>
      <Form.Range
        min={MIN_SOUND_VOLUME}
        max={MAX_SOUND_VOLUME}
        step={0.01}
        value={instruction.volume}
        disabled={disabled}
        onChange={event => onUpdate(Number(event.target.value), undefined)}
      />
      <output>{instruction.volume.toFixed(2)}</output>
    </label>
    <label>
      <span>Pitch</span>
      <Form.Range
        min={MIN_SOUND_PITCH}
        max={MAX_SOUND_PITCH}
        step={0.01}
        value={instruction.pitch}
        disabled={disabled}
        onChange={event => onUpdate(undefined, Number(event.target.value))}
      />
      <output>{instruction.pitch.toFixed(2)}</output>
    </label>
  </div>;
}

function DelayCardControls({
  instruction,
  disabled,
  onUpdate,
}: {
  instruction: SequencerInstruction;
  disabled: boolean;
  onUpdate: (ticks: number) => void;
}) {
  if (instruction.type !== 'delay') return null;

  return <div className="sequencer-delay-control">
    <label htmlFor={`delay-${instruction.id}`}>Ticks</label>
    <Form.Control
      id={`delay-${instruction.id}`}
      type="number"
      min={MIN_DELAY_TICKS}
      max={MAX_DELAY_TICKS}
      step={1}
      value={instruction.ticks}
      disabled={disabled}
      onChange={event => onUpdate(Number(event.target.value))}
    />
  </div>;
}

function DragPreview({ dragData }: { dragData: DragData }) {
  return <div className={classNames('sequencer-card', 'sequencer-card--drag-overlay')}>
    <div className="sequencer-card__top">
      <span className="sequencer-card__handle"><i className="bi-grip-vertical"/></span>
      <div className="sequencer-card__title">
        <strong>{dragData.displayName || 'Wait'}</strong>
        {dragData.soundEvent && <code>{dragData.soundEvent}</code>}
      </div>
    </div>
  </div>;
}

function getDropIndex(overId: UniqueIdentifier | undefined, instructions: SequencerInstruction[]) {
  if (typeof overId === 'undefined' || overId === null) return undefined;
  const id = String(overId);

  if (id === 'sequence-empty' || id === 'sequence-list') return instructions.length;

  if (id.startsWith('slot:')) {
    const index = Number(id.slice(5));
    return Number.isFinite(index) ? Math.max(0, Math.min(index, instructions.length)) : undefined;
  }

  const instructionIndex = instructions.findIndex(instruction => instruction.id === id);
  return instructionIndex === -1 ? undefined : instructionIndex;
}

function getInstructionTitle(instruction: SequencerInstruction) {
  if (instruction.type === 'delay') return 'Wait';
  return SoundLibrary.getCategory(instruction.soundEvent)?.displayName || instruction.soundEvent;
}

function classNames(...names: Array<string | false | undefined>) {
  return names.filter(Boolean).join(' ');
}

const sequencerCollisionDetection: CollisionDetection = args => {
  const collisions = pointerWithin(args);
  const exactCollisions = collisions.filter(collision => String(collision.id) !== 'sequence-list');

  return exactCollisions.length ? exactCollisions : collisions;
};
